import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { format, parseISO } from 'date-fns';

import { loadKycContactFileUrl } from 'state/kyc/actions';
import { isAlicloudUrl } from 'utils/fileUtils';
import DetailSection from './DetailSection';
import ImagePreviewModal from './ImagePreviewModal';
import styles from './ContactDetail.module.css';

const renderValueOrDash = value =>
  value === null || value === undefined || value === '' ? '-' : value;

const renderDateOrDash = value =>
  value ? format(parseISO(value), 'dd MMM yyyy') : '-';

function ContactDetail({ contact, submissionDate }) {
  const dispatch = useDispatch();
  const { fileUrls } = useSelector(state => state.kyc.detail);
  const [previewFileId, setPreviewFileId] = useState(null);

  const previewUrl = previewFileId && fileUrls[previewFileId];
  const showPreviewModal = !!previewUrl && isAlicloudUrl(previewUrl);

  const onFileClick = fileId => {
    setPreviewFileId(fileId);
    dispatch(loadKycContactFileUrl(contact.id, fileId));
  };

  const onClosePreview = () => setPreviewFileId(null);

  const renderFileAction = fileId => {
    if (!fileId) {
      return '-';
    }
    return (
      <a
        href="#view-file"
        onClick={e => {
          e.preventDefault();
          onFileClick(fileId);
        }}
      >
        View file
      </a>
    );
  };

  const ktp = contact.ktp || {};
  const npwp = contact.npwp || {};
  const fileList = contact.file_list || [];

  const approvalStatusItems = [
    {
      key: 'status',
      label: 'Status',
      value: renderValueOrDash(contact.status),
    },
    {
      key: 'submissionDate',
      label: 'Submission date',
      value: renderDateOrDash(submissionDate),
    },
    {
      key: 'approvalDate',
      label: 'Approval date',
      value: renderDateOrDash(contact.approval_date),
    },
    {
      key: 'rejectionDate',
      label: 'Rejection date',
      value: renderDateOrDash(contact.rejected_date),
    },
    {
      key: 'rejectionReason',
      label: 'Rejection reason',
      value: renderValueOrDash(contact.rejection_reason),
    },
  ];

  const ktpInformationItems = [
    {
      key: 'name',
      label: 'Name on KTP',
      value: renderValueOrDash(ktp.name),
    },
    {
      key: 'number',
      label: 'KTP number (NIK)',
      value: renderValueOrDash(ktp.number),
    },
    {
      key: 'address',
      label: 'KTP address',
      value: renderValueOrDash(ktp.address),
    },
    {
      key: 'photo',
      label: 'KTP photo',
      value: renderFileAction(ktp.file_id),
    },
  ];

  const businessEntityItems = [
    {
      key: 'name',
      label: 'Business entity name',
      value: renderValueOrDash(contact.business_entity),
    },
    {
      key: 'npwpName',
      label: 'NPWP name',
      value: renderValueOrDash(npwp.name),
    },
    {
      key: 'npwpNumber',
      label: 'NPWP number',
      value: renderValueOrDash(npwp.number),
    },
    {
      key: 'npwpAddress',
      label: 'NPWP address',
      value: renderValueOrDash(npwp.address),
    },
    {
      key: 'npwpPhoto',
      label: 'NPWP photo',
      value: renderFileAction(npwp.file_id),
    },
  ];

  const fileItems = fileList.map(file => ({
    key: file.file_id,
    label: file.title,
    value: renderFileAction(file.file_id),
  }));

  const merchantContactItems = [
    {
      key: 'typeOfBusiness',
      label: 'Type of business',
      value: renderValueOrDash(contact.type_of_business),
    },
    {
      key: 'businessEmail',
      label: 'Business email',
      value: renderValueOrDash(contact.business_email),
    },
    {
      key: 'registeredEmail',
      label: 'Registered email',
      value: renderValueOrDash(contact.email),
    },
    {
      key: 'picPhone',
      label: 'PIC phone',
      value: renderValueOrDash(contact.pic_phone),
    },
  ];

  const bankAccountItems = [
    {
      key: 'bankName',
      label: 'Bank name',
      value: renderValueOrDash(contact.bank_name),
    },
    {
      key: 'accountNumber',
      label: 'Account number',
      value: renderValueOrDash(contact.bank_account_number),
    },
    {
      key: 'accountOwnerName',
      label: 'Account owner name',
      value: renderValueOrDash(contact.bank_account_owner),
    },
  ];

  return (
    <>
      {/* business_name is often blank for individual (non-corporate)
      contacts, so fall back to the KTP holder's name, then the KYC number */}
      <h2 style={{ margin: 0 }}>
        {contact.business_name || ktp.name || contact.name}
      </h2>
      <p style={{ margin: 0 }}>BID {renderValueOrDash(contact.business_id)}</p>
      <p style={{ margin: 0 }}>OID {renderValueOrDash(contact.outlet_id)}</p>
      <p className={styles.identifier}>{renderValueOrDash(contact.name)}</p>

      <div className={styles['detail-sections']}>
        <DetailSection title="Approval status" items={approvalStatusItems} />
        <DetailSection title="KTP information" items={ktpInformationItems} />
        <DetailSection
          title="Business entity information"
          items={businessEntityItems}
        />
        <DetailSection
          title="Files"
          headerExtra={`${fileItems.length} attached`}
          items={fileItems}
          emptyMessage="No files attached"
        />
        <DetailSection title="Merchant contact" items={merchantContactItems} />
        <DetailSection title="Bank account" items={bankAccountItems} />
      </div>
      <ImagePreviewModal
        title="File preview"
        url={previewUrl}
        open={showPreviewModal}
        onClose={onClosePreview}
      />
    </>
  );
}

ContactDetail.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    business_name: PropTypes.string,
    business_email: PropTypes.string,
    email: PropTypes.string,
    pic_phone: PropTypes.string,
    record_type: PropTypes.string,
    type_of_business: PropTypes.string,
    approval_date: PropTypes.string,
    rejected_date: PropTypes.string,
    status: PropTypes.string,
    rejection_reason: PropTypes.string,
    bank_name: PropTypes.string,
    bank_account_number: PropTypes.string,
    bank_account_owner: PropTypes.string,
    business_entity: PropTypes.string,
    ktp: PropTypes.object,
    npwp: PropTypes.object,
    file_list: PropTypes.array,
    business_id: PropTypes.number,
    outlet_id: PropTypes.number,
  }).isRequired,
  submissionDate: PropTypes.string,
};

ContactDetail.defaultProps = {
  submissionDate: null,
};

export default ContactDetail;
